import React, { useState, useEffect } from 'react';
import Scribble from 'components/plateviewer/plate/Scribble';
import RegionOfInterest from 'components/plateviewer/plate/RegionOfInterest';
import Annotations from 'components/plateviewer/plate/Annotations';
import Scores from 'components/plateviewer/plate/Scores';
import { DisplayModes, GetDisplayMode, compareWithScoreGroupId } from 'utils/Global';
import { AnnotationLoaderDetailsProps } from 'business/models/CustomModels';
import { RMSvgIconImage } from 'components/customComponents/CommonComponents/RMSvgIcon';
import DisableImageDrop from './DisableImageDrop';
import Histogram from './Histogram';

const createHistogramImages = (hstgDetail, plate) => {
  const cells = [];

  if (!hstgDetail) {
    return cells;
  }
  for (let x1 = 0; x1 < hstgDetail.length; x1 += 1) {
    const wellRect = plate.wellRects[hstgDetail[x1] - 1];
    cells.push(
      <Histogram
        id="annotionview-createhistogramimages"
        key={x1}
        loc={wellRect}
        iconwidth={(plate.width / plate.cols) * 0.15}
        iconheight={(plate.height / plate.rows) * 0.14}
        index={0}
      />,
    );
  }
  return cells;
};
const disputeIcon = RMSvgIconImage({ name: 'DisputeScoreIcon' });

const AnnotationView = ({
  plate,
  scores,
  wellNumsContainingScribbles,
  scribbles,
  annotations,
  roi,
  disabledImages,
  histogramImages,
  dropNumber,
}: AnnotationLoaderDetailsProps) => {
  if (!plate || !plate.width || !plate.height) {
    return null;
  }
  const [scoresView, setScoresView] = useState([]);
  const [scribblesView, setScribblesView] = useState([]);
  const [roiView, setRoiView] = useState([]);
  const [annotationsView, setAnnotationsView] = useState([]);
  const [disabledImagesView, setDisabledImagesView] = useState([]);
  const [histogramImagesView, setHistorgramImagesView] = useState([]);
  const displayMode = GetDisplayMode();
  const widthPerofRect = displayMode === DisplayModes.WELLINGREDIENT ? 0.1 : 0.12;
  const heightPerofRect = displayMode === DisplayModes.WELLINGREDIENT ? 0.1 : 0.12;

  useEffect(() => {
    function createScores(scoresDetail) {
      const cells = [];
      if (!scoresDetail) {
        return null;
      }

      for (let iter = 0; iter < scoresDetail.length; iter++) {
        if (scoresDetail[iter].length > 0) {
          const wellRect =
            displayMode === DisplayModes.WELLINGREDIENT
              ? plate.wellRectsPadded[scoresDetail[iter][0].wellNumber - 1]
              : plate.wellRects[scoresDetail[iter][0].wellNumber - 1];
          const scoresOfWellDrops = scoresDetail[iter];
          const autoScoresOfWellDrops = scoresOfWellDrops.filter(
            (score) => score.isAutoScore === true,
          );
          const manualScoresOfWellDrops = scoresOfWellDrops.filter(
            (score) => score.isAutoScore === false,
          );
          const sortedManualScores = manualScoresOfWellDrops.sort(
            compareWithScoreGroupId,
          );
          const sortedAutoScores = autoScoresOfWellDrops.sort(compareWithScoreGroupId);
          let sortedWellDropScores = [...sortedManualScores, ...sortedAutoScores];
          if (wellRect.w * widthPerofRect * scoresDetail[iter].length > wellRect.w) {
            const maxNoOScores = wellRect.w / (wellRect.w * widthPerofRect);
            sortedWellDropScores = sortedWellDropScores.slice(0, maxNoOScores);
          }
          const xPos = wellRect.x;
          cells.push(
            <Scores
              id="annotionview-createscores"
              x={xPos}
              y={wellRect.y}
              icon={disputeIcon}
              width={wellRect.w * widthPerofRect}
              height={wellRect.h * heightPerofRect}
              scoresOfWellDrops={sortedWellDropScores}
            />,
          );
        }
      }
      return cells;
    }

    function createScribbles(scribbles, wellNumsContainingScribbles, w, h, r, c) {
      const cells = [];
      if (!scribbles) {
        return cells;
      }
      const wellNumbersWithScribbles = wellNumsContainingScribbles.filter(
        (item) => item.dropNum == dropNumber,
      );
      for (let iter = 0; iter < scribbles.length; iter++) {
        const wellRect = plate.wellRects[wellNumbersWithScribbles[iter].wellNum - 1];
        cells.push(
          <Scribble
            key={iter}
            loc={wellRect}
            iconwidth={(w / c) * 0.15}
            iconheight={(h / r) * 0.14}
            index={4}
          />,
        );
      }
      return cells;
    }

    function createROI(roiDetail, dropNumber, w, h, r, c) {
      const cells = [];
      if (!roiDetail) {
        return cells;
      }

      for (let x1 = 0; x1 < roiDetail.length; x1++) {
        const roiIndex = roiDetail[x1];
        if (roiIndex.dropNumber === dropNumber) {
          const wellRect = plate.wellRects[roiIndex.wellNumber - 1];
          cells.push(
            <RegionOfInterest
              id="annotionview-createroi"
              key={x1}
              loc={wellRect}
              iconwidth={(w / c) * 0.15}
              iconheight={(h / r) * 0.14}
              index={3}
            />,
          );
        }
      }
      return cells;
    }

    function createAnnotation(annotationDetail, dropNumber, w, h, r, c) {
      const cells = [];

      if (!annotationDetail) {
        return cells;
      }
      for (let x1 = 0; x1 < annotationDetail.length; x1 += 1) {
        const annotIndex = annotationDetail[x1];
        if (annotIndex.dropNumber === dropNumber) {
          const wellRect = plate.wellRects[annotIndex.wellNumber - 1];
          cells.push(
            <Annotations
              id="annotionview-createannotions"
              key={x1}
              loc={wellRect}
              iconwidth={(w / c) * 0.15}
              iconheight={(h / r) * 0.14}
              index={1}
            />,
          );
        }
      }
      return cells;
    }

    const createDisabledImages = (imagesDetail, w, h, r, c) => {
      const cells = [];

      if (!imagesDetail) {
        return cells;
      }
      for (let x1 = 0; x1 < imagesDetail.length; x1 += 1) {
        const wellRect = plate.wellRects[imagesDetail[x1] - 1];
        cells.push(
          <DisableImageDrop
            id="annotionview-createdisableimagedrop"
            key={x1}
            loc={wellRect}
            iconwidth={(w / c) * 0.15}
            iconheight={(h / r) * 0.14}
            index={2}
          />,
        );
      }
      return cells;
    };

    setScoresView(createScores(scores));

    setScribblesView(
      createScribbles(
        scribbles,
        wellNumsContainingScribbles,
        plate.width,
        plate.height,
        plate.rows,
        plate.cols,
      ),
    );

    setRoiView(
      createROI(roi, dropNumber, plate.width, plate.height, plate.rows, plate.cols),
    );

    setAnnotationsView(
      createAnnotation(
        annotations,
        dropNumber,
        plate.width,
        plate.height,
        plate.rows,
        plate.cols,
      ),
    );

    setDisabledImagesView(
      createDisabledImages(
        disabledImages,
        plate.width,
        plate.height,
        plate.rows,
        plate.cols,
      ),
    );
  }, [
    scores,
    scribbles,
    wellNumsContainingScribbles,
    annotations,
    roi,
    disputeIcon,
    disabledImages,
    plate.width,
    plate.height,
  ]);

  useEffect(() => {
    setHistorgramImagesView(createHistogramImages(histogramImages, plate));
  }, [histogramImages, plate?.width]);

  return (
    <>
      {scoresView}
      {scribblesView}
      {annotationsView}
      {roiView}
      {disabledImagesView}
      {histogramImagesView}
    </>
  );
};

AnnotationView.propTypes = {};

export default AnnotationView;
